import { useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { isTokenExpiringSoon } from '../utils/jwt';

/**
 * Hook to automatically refresh JWT token before it expires
 * Checks every minute if token is expiring soon (within 3 minutes)
 * and refreshes it proactively
 */
export function useTokenRefresh() {
  const refreshAccessToken = useStore((s) => s.refreshAccessToken);
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const accessToken = useStore((s) => s.accessToken);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only set up refresh if user is authenticated
    if (!isAuthenticated()) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkAndRefreshToken = async () => {
      try {
        if (!accessToken) {
          return;
        }

        // In DEV use same time as backend (mock) for token expiry check
        let nowMs: number | undefined;
        if (import.meta.env.DEV) {
          try {
            const { timeProvider } = await import('../services/TimeProvider');
            nowMs = (await timeProvider.getCurrentTime()).getTime();
          } catch {
            // fallback: use real time
          }
        }
        // Check if token is expiring soon (within 3 minutes)
        if (isTokenExpiringSoon(accessToken, 3, nowMs)) {
          await refreshAccessToken();
        }
      } catch (error) {
        console.error('❌ useTokenRefresh: Failed to refresh token:', error);
        // CRITICAL: If refresh fails, clear auth state and redirect to login
        // This handles cases where backend restarted or refresh token is invalid
        const { useStore } = await import('../../store');
        const { accessTokenStore } = await import('../auth/accessTokenStore');
        sessionStorage.clear();
        accessTokenStore.clear();
        useStore.setState({ 
          userProfile: null, 
          accessToken: null, 
          treeData: [] 
        });
        
        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        // Clear interval since auth is invalid
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    // Check immediately
    checkAndRefreshToken();

    // Then check every minute
    intervalRef.current = setInterval(checkAndRefreshToken, 60 * 1000); // 1 minute

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, refreshAccessToken, accessToken]);
}
